import {memo, useEffect, useState, useCallback, useRef} from 'react'
import {Formik} from 'formik'
import classNames from 'classnames'
import Tooltip from '../../components/core/tooltip'
import Input from '../../components/core/input'
import {HelpIcon, SecondaryTrashIcon} from '../../icons'
import {colors} from '../../utils/colors'
import {logManagementFiltersSchema} from '../../utils/validation'
import TextHelper from '../../utils/text'
import {SimpleBlueButton} from '../../components/core/button'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'

const MESSAGE_FIELD = 'message'
const names = {
    field_name: 'field_name',
    value: 'value',
    condition: 'condition'
}

const FilterFields = ({logsFields, isDarkMode = false, fieldPairs, setFieldsPairs, onApply, onClear}) => {
    const data = useNamespaceTranslations('condition-types')
    const [focusedText, setFocusedText] = useState('')
    const [focusedIndex, setFocusedIndex] = useState(null)
    const [isNewFieldClicked, setNewFieldClicked] = useState(false)
    const [fieldSuggestions, setFieldSuggestions] = useState([])
    const text = useNamespaceTranslations('log-management')

    const formikRef = useRef()

    const sortedLogsFields = [...logsFields].sort((a, b) =>
        a.startsWith('extra.') === b.startsWith('extra.') ? 0 : a.startsWith('extra.') ? 1 : -1
    )

    const lastIndex = fieldPairs.length - 1

    const addEmptyFieldPairIfNeeded = useCallback(() => {
        const last = fieldPairs[lastIndex]
        if (last?.field_name || last?.value) {
            setFieldsPairs(prev => [
                ...prev,
                {field_name: '', condition: data.log_management_conditions.values.equal, value: ''}
            ])
        }
    }, [fieldPairs, lastIndex])

    const updatePair = (index, updates) => {
        setFieldsPairs(prev => prev.map((p, i) => (i === index ? {...p, ...updates} : p)))
    }

    const handleInputChange = (index, key, value) => {
        updatePair(index, {[key]: value})

        if (key === names.field_name) {
            const matches = value
                ? logsFields.filter(f => f.toLowerCase().includes(value.toLowerCase()))
                : []
            setFieldSuggestions(matches)
        }

        if (index === lastIndex) addEmptyFieldPairIfNeeded()
    }

    const removeFieldPair = index => {
        const updated = fieldPairs.filter((_, i) => i !== index)
        setFieldsPairs(updated)
        setFocusedIndex(updated.length - 1)
    }

    useEffect(() => {
        if (focusedIndex !== null) {
            updatePair(focusedIndex, {field_name: focusedText})
        }
    }, [focusedText])

    useEffect(() => {
        if (isNewFieldClicked && focusedIndex === lastIndex) {
            addEmptyFieldPairIfNeeded()
            setNewFieldClicked(false)
        }
        setFocusedIndex(lastIndex)
    }, [isNewFieldClicked])

    useEffect(() => {
        formikRef?.current?.setValues({fieldPairs})
    }, [fieldPairs])

    return (
        <Formik
            initialValues={{fieldPairs}}
            validationSchema={logManagementFiltersSchema}
            onSubmit={onApply}
            validateOnBlur={false}
            validateOnChange={false}
            innerRef={formikRef}
        >
            {({handleSubmit, errors, values}) => (
                <div className='filter-section'>
                    <div className='field-builder'>
                        <div className={classNames('field-input-area', {dark: isDarkMode})}>
                            <form onSubmit={handleSubmit} className='field-condition-list'>
                                {values.fieldPairs.map((pair, index) => (
                                    <div
                                        className={classNames('field-condition-row', {dark: isDarkMode})}
                                        key={index}
                                    >
                                        <div className='field-row-wrapper'>
                                            <Input
                                                type='text'
                                                name={names.field_name}
                                                value={pair.field_name}
                                                placeholder={text.field}
                                                error={errors.fieldPairs?.[index]?.field_name}
                                                onFocus={() => setFocusedIndex(index)}
                                                onChange={e => handleInputChange(index, names.field_name, e.target.value)}
                                            />

                                            {index === focusedIndex && fieldSuggestions.length > 0 && (
                                                <div className={classNames('field-suggestions', {dark: isDarkMode})}>
                                                    <h4>{text.suggestions_title}</h4>
                                                    <ul>
                                                        {fieldSuggestions.map((field, idx) => (
                                                            <li
                                                                key={idx}
                                                                title={field}
                                                                className='suggestion-option'
                                                                onClick={() => {
                                                                    updatePair(index, {field_name: field})
                                                                    setFieldSuggestions([])
                                                                }}
                                                            >
                                                                {TextHelper.textLengthChecker(field, 30)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            <Input
                                                name={names.condition}
                                                inputIsDisabled
                                                placeholder={text.condition}
                                                value={
                                                    pair.field_name === MESSAGE_FIELD
                                                        ? data.log_management_conditions.values.contains.label
                                                        : pair.condition.label
                                                }
                                                error={errors.fieldPairs?.[index]?.condition}
                                            />

                                            <Input
                                                type='text'
                                                name={names.value}
                                                value={pair.value}
                                                placeholder={text.value}
                                                error={errors.fieldPairs?.[index]?.value}
                                                onChange={e => handleInputChange(index, names.value, e.target.value)}
                                            />
                                        </div>

                                        {index === 0 ? (
                                            <Tooltip
                                                placement='left'
                                                trigger='click'
                                                content={<span dangerouslySetInnerHTML={{__html: text.json_info}} />}
                                            >
                                                <HelpIcon stroke={colors.colorBgFilter} width='22' height='22' />
                                            </Tooltip>
                                        ) : (
                                            <button
                                                type='button'
                                                onClick={() => removeFieldPair(index)}
                                                className='field-remove-button'
                                            >
                                                <SecondaryTrashIcon stroke={colors.colorRed} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </form>
                        </div>

                        <div className={classNames('field-suggestion-list', {dark: isDarkMode})}>
                            <h3 className={classNames('field-suggestion-header', {dark: isDarkMode})}>
                                {text.available_fields}
                            </h3>
                            {sortedLogsFields.map((field, idx) => (
                                <span
                                    key={idx}
                                    className={classNames('suggestion-text', {
                                        disabled: field.startsWith('extra.')
                                    })}
                                    title={field}
                                    onClick={() => {
                                        setFocusedText(field)
                                        setNewFieldClicked(true)
                                    }}
                                    onMouseEnter={() => {
                                        setFocusedText(field)
                                        setNewFieldClicked(false)
                                    }}
                                    onMouseLeave={() => !isNewFieldClicked && setFocusedText('')}
                                >
                                    {TextHelper.textLengthChecker(field, 32)}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className='apply-filter'>
                        <SimpleBlueButton onClick={onClear} className='cancel-button'>
                            {text.clear}
                        </SimpleBlueButton>
                        <SimpleBlueButton onClick={handleSubmit} type='submit'>
                            {text.apply}
                        </SimpleBlueButton>
                    </div>
                </div>
            )}
        </Formik>
    )
}

export default memo(FilterFields)
